import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Wallet, BarChart3, Fingerprint, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hapticSelection, hapticSuccess } from '../../lib/haptics';
import { verifyBiometric } from '../../lib/biometric';
import { useIsDesktop } from '../../lib/useIsDesktop';
import Contributions from './Contributions';
import Finances from './Finances';

/**
 * Hub Finances & Cotisations — affichage séparé selon le rôle :
 *  - Membre : suivi de ses cotisations personnelles (Contributions).
 *  - Trésorier / Admin : onglet supplémentaire « Comptabilité » (Finances),
 *    protégé par vérification biométrique (empreinte / Face ID) sur mobile.
 */
export default function FinancesHub() {
  const { isAdmin, isResponsable } = useAuth();
  const canManage = isAdmin || isResponsable;
  const isDesktop = useIsDesktop();

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'mine');
  const [unlocked, setUnlocked] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);

  const unlockAccounting = useCallback(async () => {
    setAuthenticating(true);
    const ok = await verifyBiometric('Accès à la comptabilité du Dahira');
    setAuthenticating(false);
    if (ok) {
      setUnlocked(true);
      hapticSuccess();
    }
    return ok;
  }, []);

  const onChange = useCallback(
    async (value) => {
      hapticSelection();
      // Le passage vers la comptabilité exige une authentification biométrique.
      if (value === 'accounting' && !unlocked) {
        const ok = await unlockAccounting();
        if (!ok) return; // on reste sur l'onglet courant si échec
      }
      setTab(value);
      setSearchParams({ tab: value }, { replace: true });
    },
    [unlocked, unlockAccounting, setSearchParams]
  );

  // Membre simple : pas d'onglets, uniquement ses cotisations.
  if (!canManage) {
    return <Contributions />;
  }

  // Bloc « Comptabilité » (protégé par biométrie), réutilisé mobile et desktop.
  const accountingBlock = unlocked ? (
    <Finances />
  ) : (
    <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed bg-muted/30 px-6 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Fingerprint className="h-8 w-8" />
      </div>
      <div>
        <h3 className="text-lg font-bold">Espace comptable protégé</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
          La balance financière et la validation des reçus sont protégées par
          authentification biométrique.
        </p>
      </div>
      <Button onClick={unlockAccounting} disabled={authenticating} className="gap-2">
        <Fingerprint className="h-4 w-4" />
        {authenticating ? 'Vérification…' : 'Déverrouiller'}
      </Button>
    </div>
  );

  // Sur desktop, « Mes cotisations » est déjà dans la barre latérale
  // (« Cotisations ») → on n'affiche ici que la comptabilité, sans onglets.
  if (isDesktop) {
    return <div className="space-y-4">{accountingBlock}</div>;
  }

  return (
    <Tabs value={tab} onValueChange={onChange} className="space-y-4">
      <TabsList className="grid w-full grid-cols-2 h-auto p-1 rounded-2xl bg-muted shadow-sm ring-1 ring-border/50 md:sticky md:top-0 z-30 backdrop-blur">
        <TabsTrigger value="mine" className="gap-2 py-2 rounded-xl text-xs sm:text-sm data-[state=active]:shadow">
          <Wallet className="h-4 w-4" />
          Mes cotisations
        </TabsTrigger>
        <TabsTrigger value="accounting" className="gap-2 py-2 rounded-xl text-xs sm:text-sm data-[state=active]:shadow">
          <BarChart3 className="h-4 w-4" />
          Comptabilité
          {!unlocked && <Lock className="h-3 w-3 opacity-60" />}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="mine" className="mt-0 focus-visible:outline-none">
        <Contributions />
      </TabsContent>

      <TabsContent value="accounting" className="mt-0 focus-visible:outline-none">
        {accountingBlock}
      </TabsContent>
    </Tabs>
  );
}
